import React, { useState, useEffect } from 'react';
import api from '../api';
import { getMyCompanies } from '../api';
import UserSelector from './UserSelector';

const CATEGORY_COLOR = {
  Legal:      { bg: '#fef2f2', text: '#dc2626' },
  Licensing:  { bg: '#fffbeb', text: '#d97706' },
  Location:   { bg: '#eff6ff', text: '#2563eb' },
  Finance:    { bg: '#f0fdf4', text: '#16a34a' },
  Staffing:   { bg: '#f5f3ff', text: '#7c3aed' },
  Marketing:  { bg: '#fdf2f8', text: '#db2777' },
  Operations: { bg: '#f8fafc', text: '#64748b' },
};

const LOADING_MESSAGES = [
  "Analyzing your business idea...",
  "Searching official county and state records...",
  "Looking up licenses and permit forms...",
  "Structuring projects and phases...",
  "Sequencing tasks and deadlines...",
  "Almost there — finalizing your plan...",
];

export default function QuickStartWizard({ onClose, onCreated }) {
  const [step,      setStep]      = useState(1);
  const [idea,      setIdea]      = useState('');
  const [companies,   setCompanies]   = useState([]);
  const [companyId,   setCompanyId]   = useState('');
  const [state,     setState]     = useState('');      // full state name (sent to AI)
  const [stateCode, setStateCode] = useState('');       // 2-letter code (used to load location data)
  const [county,    setCounty]    = useState('');
  const [city,      setCity]      = useState('');
  const [loading,   setLoading]   = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [error,     setError]     = useState('');
  const [plan,      setPlan]      = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState(null); // 'up' | 'down' | null
  const [creating,  setCreating]  = useState(false);
  const [createProgress, setCreateProgress] = useState({ current: 0, total: 0 });
  const [assignedUsers, setAssignedUsers] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      return stored?.id ? [parseInt(stored.id)] : [];
    } catch { return []; }
  });

  // Location intelligence
  const [allStates,    setAllStates]    = useState([]);   // [{code, name}]
  const [countyIndex,  setCountyIndex]  = useState(null); // { "dekalb": ["GA","IL",...] }
  const [stateData,    setStateData]    = useState(null); // { state, counties: { "DeKalb County": [cities] } }
  const [countySuggestions, setCountySuggestions] = useState(null); // ambiguity prompt data
  const [locationLoading, setLocationLoading] = useState(false);

  // Load the states list + county lookup index once
  useEffect(() => {
    fetch('/us-locations/_states.json').then(r => r.json()).then(setAllStates).catch(() => {});
    fetch('/us-locations/_county_index.json').then(r => r.json()).then(setCountyIndex).catch(() => {});
    getMyCompanies().then(res => {
      const list = res.data || [];
      setCompanies(list);
      if (list.length === 1) setCompanyId(list[0].id);
    }).catch(() => {});
  }, []);

  // Load a specific state's county/city data whenever the state code changes
  useEffect(() => {
    if (!stateCode) { setStateData(null); return; }
    setLocationLoading(true);
    fetch(`/us-locations/${stateCode}.json`)
      .then(r => r.json())
      .then(data => { setStateData(data); setLocationLoading(false); })
      .catch(() => setLocationLoading(false));
  }, [stateCode]);

  // Try to detect a county mentioned in the idea text (e.g. "start a restaurant in dekalb county")
  const detectCountyFromIdea = () => {
    if (!idea || !countyIndex) return;
    const match = idea.match(/([A-Za-z][A-Za-z\s]{1,30}?)\s+county/i);
    if (!match) return;
    const rawName = match[1].trim().toLowerCase();
    const matchStates = countyIndex[rawName];
    if (!matchStates || !matchStates.length) return;

    if (matchStates.length === 1) {
      const code = matchStates[0];
      const found = allStates.find(s => s.code === code);
      if (found) {
        setStateCode(code);
        setState(found.name);
        // County gets selected once that state's data loads (see effect below)
        setCountySuggestions({ pendingCounty: rawName });
      }
    } else {
      // Ambiguous — ask the user to confirm which state they meant
      setCountySuggestions({
        rawName,
        options: matchStates.map(code => allStates.find(s => s.code === code)).filter(Boolean),
      });
    }
  };

  // Once state data loads after an auto-detect, select the matching county by proper display name
  useEffect(() => {
    if (!stateData || !countySuggestions?.pendingCounty) return;
    const target = countySuggestions.pendingCounty;
    const match = Object.keys(stateData.counties).find(c =>
      c.toLowerCase().replace(' county', '').replace(' parish', '').replace(' borough', '') === target
    );
    if (match) setCounty(match);
    setCountySuggestions(null);
  }, [stateData, countySuggestions]);

  const selectSuggestedState = (opt) => {
    setStateCode(opt.code);
    setState(opt.name);
    setCountySuggestions({ pendingCounty: countySuggestions.rawName });
  };

  const sendFeedback = async (rating) => {
    if (!plan?.log_id || feedbackGiven) return;
    setFeedbackGiven(rating); // optimistic — feels instant, low stakes if it fails silently
    try {
      await api.post('/gpf/v1/ai-agent/quickstart-feedback', { log_id: plan.log_id, feedback: rating });
    } catch (e) { console.error('Feedback failed to save:', e); }
  };

  const totalTasks = plan?.projects?.reduce((sum, p) => sum + p.tasks.length, 0) || 0;

  const generatePlan = async () => {
    if (!idea.trim())  { setError('Please describe what you want to start.'); return; }
    if (!state)        { setError('Please select a state.'); return; }
    setError(''); setLoading(true); setLoadingMsgIdx(0);

    const msgInterval = setInterval(() => {
      setLoadingMsgIdx(prev => Math.min(prev + 1, LOADING_MESSAGES.length - 1));
    }, 3500);

    try {
      const res = await api.post('/gpf/v1/ai-agent/quickstart', { idea, state, county, city });
      setPlan(res.data);
      setFeedbackGiven(null);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to generate plan. Please try again.');
    } finally {
      clearInterval(msgInterval);
      setLoading(false);
    }
  };

  const createEverything = async () => {
    setCreating(true); setError('');
    const total = totalTasks + plan.projects.length; // projects + tasks
    let done = 0;
    setCreateProgress({ current: 0, total });

    try {
      let firstProjectId = null;
      for (const proj of plan.projects) {
        let projRes;
        try {
          projRes = await api.post('/wp/v2/projects', {
            title:   proj.project_name,
            content: `${proj.project_description}\n\nLocation: ${plan.location}`,
            status:  'publish',
            meta: {
              gpf_status:        'not_started',
              gpf_priority:      'medium',
              gpf_assigned_users: JSON.stringify(assignedUsers),
              gpf_source:        'quickstart',
              gpf_company_id:    companyId || 0,
            },
          });
        } catch (e) {
          const msg = e.response?.data?.message || e.message;
          throw new Error(`Failed to create project "${proj.project_name}": ${msg}`);
        }
        const projectId = projRes.data.id;
        if (!firstProjectId) firstProjectId = projectId;
        done++; setCreateProgress({ current: done, total });

        const today = new Date();
        for (const t of proj.tasks) {
          const due = new Date(today);
          due.setDate(due.getDate() + (t.due_offset_days || 0));
          const dueStr = due.toISOString().split('T')[0];

          try {
            await api.post('/wp/v2/tasks', {
              title:   t.title,
              content: t.description || '',
              status:  'publish',
              meta: {
                gpf_project_id:     projectId,
                gpf_status:         'not_started',
                gpf_priority:       t.priority || 'medium',
                gpf_end_date:       dueStr,
                gpf_assigned_users: JSON.stringify(assignedUsers),
                gpf_form_name:      t.form_name || '',
                gpf_form_url:       t.form_url  || '',
                gpf_source:         'quickstart',
              },
            });
          } catch (e) {
            const msg = e.response?.data?.message || e.message;
            throw new Error(`Failed to create task "${t.title}": ${msg}`);
          }
          done++; setCreateProgress({ current: done, total });
        }
      }

      onCreated && onCreated(firstProjectId);
      onClose();
    } catch (e) {
      setError(e.message || 'Some items failed to create. Please check your Projects page.');
      setCreating(false);
      return; // stay on this screen so the person can read the error before closing
    } finally { setCreating(false); }
  };

  return (
    <div style={styles.overlay} onClick={creating ? undefined : onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>🚀 Quick Start</h2>
            <p style={styles.sub}>Describe your idea — we'll build the project plan for you</p>
          </div>
          {!creating && <button onClick={onClose} style={styles.closeBtn}>✕</button>}
        </div>

        <div style={styles.body}>
          {error && <div style={styles.errorBox}>⚠️ {error}</div>}

          {step === 1 && (
            <>
              <label style={styles.label}>What do you want to start?</label>
              <textarea
                style={{ ...styles.input, minHeight: 90, resize: 'vertical' }}
                placeholder="e.g. Start a restaurant business"
                value={idea}
                onChange={e => setIdea(e.target.value)}
                autoFocus
              />
              {companies.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <label style={styles.label}>Which company is this for?</label>
                  {companies.length === 1 ? (
                    <div style={{ ...styles.input, background: '#f8fafc', color: '#64748b', display: 'flex', alignItems: 'center' }}>
                      🏢 {companies[0].name}
                    </div>
                  ) : (
                    <select style={styles.input} value={companyId} onChange={e => setCompanyId(e.target.value)}>
                      <option value="">Select company...</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button style={styles.nextBtn} onClick={() => {
                  if (!idea.trim()) { setError('Please describe your idea first.'); return; }
                  if (companies.length > 1 && !companyId) { setError('Please select which company this plan is for.'); return; }
                  detectCountyFromIdea(); setStep(2);
                }}>
                  Next →
                </button>
              </div>
            </>
          )}

          {step === 2 && !loading && (
            <>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
                Where will this happen? We'll tailor licenses & permits to your location.
              </p>

              {countySuggestions?.options && (
                <div style={styles.suggestBox}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#92400e', margin: '0 0 8px' }}>
                    We found "{countySuggestions.rawName.replace(/\b\w/g, c => c.toUpperCase())} County" in {countySuggestions.options.length} states — which did you mean?
                  </p>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {countySuggestions.options.map(opt => (
                      <button key={opt.code} style={styles.suggestChip} onClick={() => selectSuggestedState(opt)}>
                        {opt.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div>
                  <label style={styles.label}>State *</label>
                  <select
                    style={styles.input}
                    value={stateCode}
                    onChange={e => {
                      const code = e.target.value;
                      const found = allStates.find(s => s.code === code);
                      setStateCode(code);
                      setState(found?.name || '');
                      setCounty(''); setCity('');
                      setCountySuggestions(null);
                    }}
                  >
                    <option value="">Select state...</option>
                    {allStates.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>County {locationLoading && '(loading...)'}</label>
                  {stateData ? (
                    <select style={styles.input} value={county} onChange={e => { setCounty(e.target.value); setCity(''); }}>
                      <option value="">Select county...</option>
                      {Object.keys(stateData.counties).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input style={styles.input} value={county} onChange={e => setCounty(e.target.value)} placeholder="Select a state first, or type manually" />
                  )}
                </div>
                <div>
                  <label style={styles.label}>City</label>
                  {stateData && county && stateData.counties[county] ? (
                    <select style={styles.input} value={city} onChange={e => setCity(e.target.value)}>
                      <option value="">Select city...</option>
                      {stateData.counties[county].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input style={styles.input} value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Pittsburgh" />
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button style={styles.backBtn} onClick={() => setStep(1)}>← Back</button>
                <button style={styles.nextBtn} onClick={generatePlan}>Generate Plan →</button>
              </div>
            </>
          )}

          {step === 2 && loading && (
            <div style={{ textAlign: 'center', padding: '30px 10px' }}>
              <div style={styles.spinner} />
              <p style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '20px 0 6px' }}>
                {LOADING_MESSAGES[loadingMsgIdx]}
              </p>
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                This can take a minute or two — please be patient while we build a thorough plan.
              </p>
            </div>
          )}

          {step === 3 && plan && !creating && (
            <>
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>{plan.overview}</p>
                    <p style={{ fontSize: 12, color: '#2563eb', margin: '8px 0 0' }}>📍 {plan.location}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>Helpful plan?</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => sendFeedback('up')}
                        disabled={!!feedbackGiven}
                        style={{ ...styles.feedbackBtn, opacity: feedbackGiven && feedbackGiven !== 'up' ? 0.35 : 1, background: feedbackGiven === 'up' ? '#dcfce7' : 'white' }}
                        title="Yes, this is a good plan"
                      >👍</button>
                      <button
                        onClick={() => sendFeedback('down')}
                        disabled={!!feedbackGiven}
                        style={{ ...styles.feedbackBtn, opacity: feedbackGiven && feedbackGiven !== 'down' ? 0.35 : 1, background: feedbackGiven === 'down' ? '#fee2e2' : 'white' }}
                        title="Not quite right"
                      >👎</button>
                    </div>
                  </div>
                </div>
                {feedbackGiven && (
                  <p style={{ fontSize: 11, color: '#16a34a', margin: '8px 0 0' }}>
                    Thanks — this helps improve future plans{feedbackGiven === 'up' ? ' like this one' : ''}.
                  </p>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Assign To</label>
                <UserSelector selected={assignedUsers} onChange={setAssignedUsers} />
                <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0' }}>
                  These team members will be assigned to every task in this plan. You can adjust individual task assignments later.
                </p>
              </div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 10px' }}>
                {plan.projects.length} projects · {totalTasks} tasks will be created:
              </p>
              <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {plan.projects.map((proj, pi) => (
                  <div key={pi}>
                    <div style={styles.projectHeader}>
                      <span style={styles.projectDot} />
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{proj.project_name}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>({proj.tasks.length} tasks)</span>
                    </div>
                    <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 8px 16px' }}>{proj.project_description}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 16 }}>
                      {proj.tasks.map((t, ti) => {
                        const cc = CATEGORY_COLOR[t.category] || CATEGORY_COLOR.Operations;
                        return (
                          <div key={ti} style={styles.taskRow}>
                            <span style={{ ...styles.catBadge, background: cc.bg, color: cc.text }}>{t.category}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t.title}</div>
                              <div style={{ fontSize: 12, color: '#94a3b8' }}>{t.description}</div>
                              {t.form_name && (
                                t.form_url ? (
                                  <a href={t.form_url} target="_blank" rel="noreferrer" style={styles.formLink}>
                                    📄 {t.form_name} ↗
                                  </a>
                                ) : (
                                  <span style={{ ...styles.formLink, color: '#94a3b8' }}>📄 {t.form_name}</span>
                                )
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>Day {t.due_offset_days}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button style={styles.backBtn} onClick={() => setStep(2)}>← Back</button>
                <button style={styles.nextBtn} onClick={createEverything}>
                  ✓ Create {plan.projects.length} Projects & {totalTasks} Tasks
                </button>
              </div>
            </>
          )}

          {creating && (
            <div style={{ textAlign: 'center', padding: '30px 10px' }}>
              <div style={styles.spinner} />
              <p style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '20px 0 10px' }}>
                Creating your projects and tasks...
              </p>
              <div style={styles.progressBg}>
                <div style={{ ...styles.progressFill, width: `${(createProgress.current / createProgress.total) * 100}%` }} />
              </div>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 0 0' }}>
                {createProgress.current} of {createProgress.total} items created — please don't close this window.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  suggestBox:   { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 14, marginBottom: 16 },
  suggestChip:  { padding: '6px 14px', background: 'white', border: '1.5px solid #f59e0b', borderRadius: 99, fontSize: 13, fontWeight: 600, color: '#92400e', cursor: 'pointer' },
  formLink:     { display: 'inline-block', marginTop: 4, fontSize: 11, color: '#2563eb', textDecoration: 'none' },
  feedbackBtn:  { width: 30, height: 30, border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:     { background: 'white', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  header:    { padding: '22px 26px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  title:     { fontSize: 19, fontWeight: 800, margin: 0 },
  sub:       { fontSize: 13, color: '#64748b', margin: '4px 0 0' },
  closeBtn:  { background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' },
  body:      { padding: '22px 26px' },
  errorBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' },
  label:     { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:     { width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  nextBtn:   { padding: '11px 22px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  backBtn:   { padding: '11px 22px', background: 'white', color: '#374151', border: '1.5px solid #e2e8f0', borderRadius: 9, fontSize: 14, cursor: 'pointer' },
  taskRow:   { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#f8fafc', borderRadius: 8 },
  catBadge:  { fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, flexShrink: 0, whiteSpace: 'nowrap' },
  projectHeader: { display: 'flex', alignItems: 'center', gap: 8 },
  projectDot:    { width: 8, height: 8, borderRadius: '50%', background: '#2563eb', flexShrink: 0 },
  spinner:   { width: 40, height: 40, border: '4px solid #eff6ff', borderTopColor: '#2563eb', borderRadius: '50%', margin: '0 auto', animation: 'gpf-spin 0.8s linear infinite' },
  progressBg:   { width: '100%', height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  progressFill: { height: '100%', background: '#2563eb', borderRadius: 99, transition: 'width 0.3s' },
};

// Inject spinner keyframes once
if (typeof document !== 'undefined' && !document.getElementById('gpf-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'gpf-spin-keyframes';
  style.textContent = '@keyframes gpf-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}